import type {ReactElement} from 'react';
import React from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {setSelectedTimelineItem} from '../../timeline-reducer';
import {getAudioItemsInOrder, getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {TimelineTrack} from '../timeline-track/timeline-track';
import {AudioTimelineItem} from '../audio-timeline-item/audio-timeline-item';
import type {TimelineItemData} from '../../timeline.types';
import {TimelineItemType, ITEM_TRACK_HEIGHT} from '../../timeline.types';
import {getMaxSubRow} from '../../utils/timeline-calculations';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';
import type {DragModifyingData, ResizeModifyingData} from '../timeline-item/timeline-item.types';

export function AudioTimelineTrack(): ReactElement | null {
  const dispatch = useAppDispatch();
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const currentPageId = useAppSelector((state) => state.posterEditor.currentPageId);
  const audioItems = useAppSelector(getAudioItemsInOrder);

  const audioTimelineItems: TimelineItemData[] = audioItems.map((audio) => {
    const startTime = audio.onPageStartTime;
    const endTime = startTime + getPlaybackDuration(audio.audioPlayer);

    return {
      id: audio.uid,
      type: TimelineItemType.AUDIO,
      startTime,
      endTime,
      subRow: audio.timelineSubRow,
      label: audio.name,
    };
  });

  const audioTotalRows = getMaxSubRow(audioTimelineItems) + 1;

  const handleAudioDragEnd = async (id: string, data: DragModifyingData): Promise<void> => {
    const audioItem = window.posterEditor?.whiteboard?.getCurrentPage()?.audioClips.audioItemsHashMap[id];
    if (!audioItem) {
      return;
    }

    const {startTime, endTime} = data;

    await audioItem.updateFromObject({
      onPageStartTime: Number(startTime.toFixed(2)),
    });

    if (endTime > (currentPageDuration ?? 0) && currentPageId) {
      const page = window.posterEditor?.whiteboard?.pages.pagesHashMap?.[currentPageId];
      if (page) {
        await page.updateFromObject({duration: endTime, isCustomDuration: true});
      }
    }
  };

  const handleAudioResizeEnd = async (id: string, data: ResizeModifyingData): Promise<void> => {
    const audioItem = window.posterEditor?.whiteboard?.getCurrentPage()?.audioClips.audioItemsHashMap[id];
    const originalData = audioItems.find((a) => a.uid === id);
    if (!audioItem || !originalData) {
      return;
    }

    let {startTime} = data;
    const {endTime} = data;
    const {speed, originalDuration, trim} = originalData.audioPlayer;

    const originalStartTime = originalData.onPageStartTime;
    const originalEndTime = originalStartTime + getPlaybackDuration(originalData.audioPlayer);

    const leftHandleMoved = startTime !== originalStartTime;
    const rightHandleMoved = endTime !== originalEndTime;

    let newTrimStart = trim.startTime;
    let newTrimEnd = trim.endTime;

    if (leftHandleMoved) {
      newTrimStart = Math.max(0, trim.startTime + (startTime - originalStartTime) * speed);
    }

    if (rightHandleMoved) {
      newTrimEnd = Math.min(originalDuration, trim.endTime + (endTime - originalEndTime) * speed);
    }

    // If startTime is negative, shift the entire item to start at 0 (keep same duration)
    if (startTime < 0) {
      startTime = 0;
    }

    await audioItem.updateFromObject({
      onPageStartTime: Number(startTime.toFixed(2)),
      audioPlayer: {
        trim: {
          isTrimmed: newTrimStart !== 0 || newTrimEnd !== originalDuration,
          startTime: Number(newTrimStart.toFixed(2)),
          endTime: Number(newTrimEnd.toFixed(2)),
        },
      },
    });

    if (endTime > (currentPageDuration ?? 0) && currentPageId) {
      const page = window.posterEditor?.whiteboard?.pages.pagesHashMap?.[currentPageId];
      if (page) {
        await page.updateFromObject({duration: endTime, isCustomDuration: true});
      }
    }
  };

  const handleAudioClick = (id: string): void => {
    void window.posterEditor?.whiteboard?.pause();
    window.posterEditor?.whiteboard?.getCurrentPage()?.audioClips.selectAudioItem(id);
    dispatch(setSelectedTimelineItem({type: TimelineItemType.AUDIO, id}));
  };

  if (audioTimelineItems.length === 0) {
    return null;
  }

  return (
    <TimelineTrack totalRows={audioTotalRows} trackHeight={ITEM_TRACK_HEIGHT}>
      {audioTimelineItems.map((item) => (
        <AudioTimelineItem
          key={item.id}
          id={item.id}
          startTime={item.startTime}
          endTime={item.endTime}
          subRow={item.subRow}
          onClick={handleAudioClick}
          onDragEnd={handleAudioDragEnd}
          onResizeEnd={handleAudioResizeEnd}
        />
      ))}
    </TimelineTrack>
  );
}
